import * as R from 'ramda';
import type { CSSProperties } from 'react';
import { validateInput } from '../../../../support/utils';
import type { Language, Metadata, TemplateOption, TemplateParts, View } from '../../../../types/journeyTypes';
import { NativeScript } from '../../ViewEditor/EmbeddedScripts';
import { BANNER_TYPES, SECONDS_PER_TIME_UNIT, TEMPLATE_TYPE_OPTIONS } from '../journeyConstants';

const { BANNER, INTERSTITIAL, BANNER_BOTTOM } = BANNER_TYPES;

export const userTemplatePatternWithGroups = /'?({{\s*\S+\s*\|(.*?)}})'?/g;

export const getLanguageTableFromTemplate = (template: string): Language => {
  const jsonRe = /<script type="application\/json">((.|\s)*?)<\/script>/;
  const match = template.match(jsonRe);
  let metadata: { language?: Language } = {};

  if (match) {
    try {
      metadata = JSON.parse(match[1]);
      // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    } catch (e) {
      metadata = {};
    }
  }

  return metadata.language || {};
};

export const removeStyleTagFromTemplate = (template: string): string => {
  // INTENG-10224
  return template.replace(/<style type="text\/css" ((.|\s)*?)>/, '').replace(/<\/style[^>]+>/, '');
};

export const getIFrameCSSStyleFromTemplate = (template: string): string => {
  const jsonRe = /<style type="text\/css" id="branch-iframe-css">((.|\s)*?)<\/style>/;
  const match = template.match(jsonRe);
  // The first thing that matches the Regex is what we want
  return match ? match[0] : '';
};

export const removeMaliciousStyleTag = (renderedTemplate: string): string => {
  const maliciousStyleRegex = /(<\/[^>]*<\/style>)|(<\/style[^>]+>)/i; // Fix INTENG-6517 , AppSec-158 and AppSec-194

  while (renderedTemplate.match(maliciousStyleRegex)) {
    renderedTemplate = renderedTemplate.replace(maliciousStyleRegex, '');
  }

  return renderedTemplate;
};

export const addLanguageToTemplate = (template: string, language?: Language): string => {
  const languageTable = language || getLanguageTableFromTemplate(template) || {};
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  const renderedTemplate = template.replace(/{{\s?(\$\w+)\s?}}/g, (match, key) => {
    return validateInput(languageTable[key]) || '';
  });
  return removeMaliciousStyleTag(renderedTemplate);
};

export const addUserTemplateDefaultsToTemplate = (template: string) => {
  // match contains the entire tag which could have outer single quotes for example: '{{ xyz | default_val }}'
  // matchGroup 1 contains everything inside of {{ }} including curly braces i.e. {{ xyz | default_val }}
  // matchGroup 2 contains everything to the right of | and before the first closing curly brace i.e. default_val
  // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  return template.replace(userTemplatePatternWithGroups, function (match, matchGroup1, matchGroup2) {
    return matchGroup2.trim();
  });
};

/*
 * Looks up full properties of type
 * @param value {string} - Enumation constant string e.g. 'banner bottom'
 */
export const findTemplateByType = (typeValue: string): TemplateOption | void => {
  if (!typeValue || typeof typeValue !== 'string') {
    return;
  }

  const lowercaseKey = typeValue.toLowerCase().split('_').join(' ');
  const options = TEMPLATE_TYPE_OPTIONS.filter((type) => type.value === lowercaseKey);

  if (!options.length) {
    return;
  }

  return options[0];
};

const findTemplateLabel = (typeValue: string): string => {
  const template = findTemplateByType(typeValue);

  if (template) {
    return template.label;
  }

  return 'missing-template';
};

/**
 * Returns the number value and unit from a value
 * i.e. '20px' => { num: '20', unit: 'px' }
 * @param {string} value
 * @return {numAndUnit} Object containing number and unit separately
 */
export const getNumAndUnitFromValue = (
  value: string
): {
  num: string | null | undefined;
  unit: string | null | undefined;
} => {
  if (typeof value !== 'string') {
    return {
      num: null,
      unit: null,
    };
  }

  let num;
  let unit;

  if (value.slice(-1) === '%') {
    num = value.slice(0, -1);
    unit = value.slice(-1);
  } else {
    // covers cases where unit is px or vh
    num = value.slice(0, -2);
    unit = value.slice(-2);
  }

  return {
    num,
    unit,
  };
};

interface DetectTypeChangeFullResult {
  diff?: boolean;
  type?: string | null;
  status?: 'success';
  memo?: string;
}

export const detectTypeChange = (metadata: Metadata, prevKey: string): DetectTypeChangeFullResult => {
  if (typeof prevKey === 'undefined' || typeof metadata.bannerHeight !== 'string') {
    return {
      type: null,
    };
  }

  const { num, unit } = getNumAndUnitFromValue(metadata.bannerHeight);
  const height = parseInt(num as string);
  var k;

  if (unit === 'px') {
    if ((height > 0 && height < 76) || (height > 76 && height < 120)) {
      k = BANNER;
    } else if (height === 76 && metadata.position === 'bottom') {
      k = BANNER_BOTTOM;
    } else if (height === 76 && metadata.position === 'top') {
      k = BANNER;
    } else if (height >= 120) {
      k = INTERSTITIAL;
    }
  } else if (unit === 'vh') {
    if ((height > 0 && height < 22) || (height > 22 && height <= 25)) {
      k = BANNER;
    } else if (height === 22 && metadata.position === 'bottom') {
      k = BANNER_BOTTOM;
    } else if (height === 22 && metadata.position === 'top') {
      k = BANNER;
      // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
    } else if (height == 100) {
      k = INTERSTITIAL;
    }
  }

  // const prevTemplateLabel = findTemplateLabel(prevKey);
  // const updatedTemplateLabel = findTemplateLabel(k);
  return {
    diff: k !== prevKey,
    type: typeof k === 'undefined' ? null : BANNER_TYPES[k],
    status: 'success',
    memo:
      typeof k !== 'undefined' ? 'View type changed from ' + findTemplateLabel(prevKey) + ' to ' + findTemplateLabel(k) : '',
  };
};

export const changeTemplateType = (view: View, metadata: Metadata): DetectTypeChangeFullResult => {
  switch (view.type) {
    case BANNER:
      return detectTypeChange(metadata, BANNER);

    case INTERSTITIAL:
      return detectTypeChange(metadata, INTERSTITIAL);

    case BANNER_BOTTOM:
      return detectTypeChange(metadata, BANNER_BOTTOM);
  }

  return {};
};

// Calculate which dropdown should be selected based on a given dismissPeriod
export const getDropdownValueFromDismissPeriod = (dismissPeriod: number): string => {
  const discreteOption = Object.keys(SECONDS_PER_TIME_UNIT).find(
    (option) => SECONDS_PER_TIME_UNIT[option] === dismissPeriod
  );
  // If there is no option that fits exactly, show the value in 'custom'
  return discreteOption || 'custom';
};

// We don't want to save keys whose values are null
export const removeNullMetadata = (metadata: Metadata): Metadata => {
  const metadataToSave = { ...metadata };
  Object.keys(metadata).forEach((key) => {
    if (metadata[key] === null) {
      delete metadataToSave[key];
    }
  });
  return metadataToSave;
};

/**
 * Build and return the default style for iFrame that is used to render the
 * the journey in
 * @param {Object} metadata
 * @return {string} CSS styles that can be applied in a <style> tag
 */
export const getIFrameDefaultCSS = (metadata?: Metadata): string => {
  const cssStyles: CSSProperties = {};

  if (metadata && metadata.bannerHeight && metadata.position && metadata.sticky) {
    if (metadata.position === 'top') {
      cssStyles.top = 0;
    } else if (metadata.position === 'bottom') {
      cssStyles.bottom = 0;
    } else {
      cssStyles.bottom = 0;
    }
  }

  if (cssStyles.bottom === undefined && cssStyles.top === undefined) {
    cssStyles.bottom = 0;
  }

  cssStyles.left = 0;
  cssStyles.right = 0;
  cssStyles.border = 0;
  cssStyles.height = '100vh';
  cssStyles['z-index'] = 99999;
  cssStyles['box-shadow'] = '0 0 5px rgba(0, 0, 0, .35)';
  cssStyles.width = '1px';
  cssStyles['min-width'] = '100%';
  // @ts-ignore
  cssStyles.position = (metadata && metadata.sticky) || 'absolute';
  return `#branch-banner-iframe {\n${Object.keys(cssStyles).reduce((acc, key) => {
    return `${acc}  ${key}: ${cssStyles[key]};\n`;
  }, '')}}`;
};

export const buildTemplateFromParts = (
  parts: TemplateParts,
  useTagged: boolean | null | undefined,
  combineOuterStyle?: boolean
): string => {
  let metadata;

  try {
    const mergedMetadata = R.merge(parts.metadata, {
      ctaText: {
        has_app: '{{$journeys_button_get_has_app}}',
        no_app: '{{$journeys_button_get_no_app}}',
      },
      globalDismissPeriod: parts.dismissPeriodRadioSelected === 'dismiss-all-journeys' ? parts.dismissPeriod : null,
      dismissRedirect: parts.dismissBehaviorRadioSelected === 'redirect-on-dismiss' ? parts.dismissRedirect : null,
    });
    metadata = JSON.stringify(removeNullMetadata(mergedMetadata));
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
  } catch (e) {
    // pass
  }

  let stylesheetWithUserTemplateDefaults;

  // Used for rendering Journeys previews in Template editor
  if (parts.style && useTagged) {
    stylesheetWithUserTemplateDefaults = addUserTemplateDefaultsToTemplate(parts.style);
  }

  return [
    '<html><head><style type="text/css" id="branch-css">',
    stylesheetWithUserTemplateDefaults || parts.style,
    '</style>',
    parts.pageSettings === 'custom' && combineOuterStyle
      ? `<style type="text/css" id="branch-iframe-css">${parts.iFrameCSSStyle}</style>`
      : '',
    '</head><body>',
    parts.bannerPosition === 'top' ? '<div id="branch-banner-spacer"></div>' : '',
    useTagged ? parts.taggedHtml : parts.html,
    NativeScript,
    metadata ? `<script type="application/json">${metadata}</script>` : '',
    '</body></html>',
  ].join('');
};

export function buildStarElem(count) {
  count = Math.max(0, Math.min(5, count));
  const spans: string[] = [];

  for (let i = 0; i < 5; i++) {
    if (count <= i) {
      spans.push('<span aria-hidden="true">☆</span>');
    } else if (count > i && count < i + 1) {
      spans.push(
        '<span aria-hidden="true">☆<span aria-hidden="true" style="position:absolute;left:0;width:50%;overflow:hidden;">★</span></span>'
      );
    } else {
      spans.push('<span aria-hidden="true">★</span>');
    }
  }

  return spans.join('');
}
